import { useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { AnimationCrossfade } from '@ui/components/AnimationCrossfade';
import { Card } from '@ui/components/Card';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { PlusIcon } from '@ui/icons/PlusIcon/PlusIcon';

import { useTabAutoRotation } from '@features/club/ui/ClubsListPromoHeader/useTabAutoRotation';

import styles from './ClubsListPromoHeader.module.css';

const CROSSFADE_SLOW = 600;
const CROSSFADE_FAST = 300;

type Tab = 'participate' | 'play' | 'analyze' | 'grow';

const TABS: {
  value: Tab;
  label: string;
  image: string;
  description: string;
}[] = [
  {
    value: 'participate',
    label: 'Участвуйте в турнирах и\u00A0матчах',
    image: '/images/club-list-promo/participate_home2.png',
    description:
      'Выбирайте подходящие турниры заранее, или зайдите в список турниров и найдите ближайший. Соревноваться с реальными соперниками просто, как никогда.',
  },
  {
    value: 'play',
    label: 'Играйте на удобной платформе',
    image: '/images/club-list-promo/play_home.png',
    description:
      'В назначенное время просто зайдите на страницу турнира с компьютера, планшета или телефона, удобный игровой интерфейс и лобби турнира не придется долго изучать.',
  },
  {
    value: 'analyze',
    label: 'Разбирайте партии в личном кабинете',
    image: '/images/club-list-promo/analyze_home.png',
    description:
      'Все сыгранные вами партии сохраняются в личном кабинете, где вы можете разобрать их по шагам со своим тренером или с ИИ',
  },
  {
    value: 'grow',
    label: 'Следите за своим прогрессом',
    image: '/images/club-list-promo/grow_home.png',
    description:
      'Следите за своим прогрессом и изменениями рейтинга с подробной историей и списком соперников',
  },
];

export function ClubsListPromoHeader() {
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0);
  const activeTab = TABS[activeTabIdx];

  const rotate = useCallback(() => {
    if (activeTabIdx === TABS.length - 1) return setActiveTabIdx(0);
    setActiveTabIdx(activeTabIdx + 1);
  }, [activeTabIdx, setActiveTabIdx]);

  const { stop, isStopped } = useTabAutoRotation(rotate);

  if (!activeTab) return null;

  return (
    <Card className={cx(styles.promoHeader, styles[activeTab.value])} flat>
      <div className={styles.title}>Вступайте в&nbsp;шахматные онлайн-клубы</div>

      <div className={styles.promoImageWrapper}>
        <AnimationCrossfade
          contentKey={activeTab.value}
          variant="cross-fade"
          timeMs={isStopped ? CROSSFADE_FAST : CROSSFADE_SLOW}
        >
          <img className={styles.promoImage} src={activeTab.image} alt={activeTab.label} />
        </AnimationCrossfade>
      </div>

      <div className={styles.description}>
        <AnimationCrossfade
          contentKey={activeTab.value}
          variant="cross-fade"
          timeMs={isStopped ? CROSSFADE_FAST : CROSSFADE_SLOW}
        >
          <span className={styles.descriptionText}>{activeTab.description}</span>
        </AnimationCrossfade>
      </div>

      <HorizontalScroll
        className={styles.tabs}
        scrollToSelector={`#tab-${activeTab.value}`}
        scrollToSelectorBlock="center"
      >
        {TABS.map((tab, idx) => (
          <div
            id={`tab-${tab.value}`}
            key={tab.value}
            className={cx(styles.tab, {
              [styles.active]: activeTab.value === tab.value,
            })}
            onClick={() => {
              stop();
              setActiveTabIdx(idx);
            }}
          >
            <div className={styles.tabTitle}>{tab.label}</div>
            <div className={styles.plusSign}>
              <PlusIcon />
            </div>
          </div>
        ))}
      </HorizontalScroll>
    </Card>
  );
}
